/**
 * Facebook Messenger Platform - Messaging Postbacks Webhook Types
 * 
 * These types represent the webhook event structure for messaging_postbacks events.
 * Triggered when a user clicks on a postback button, Get Started button, or persistent menu item.
 * 
 * @see https://developers.facebook.com/docs/messenger-platform/reference/webhook-events/messaging_postbacks
 */

import {
  BaseWebhookEvent,
  WebhookEventType,
  WebhookPayload,
  WebhookSender,
  WebhookRecipient,
} from './base-types';

// Note: PostbackSender and PostbackRecipient are now replaced by
// WebhookSender and WebhookRecipient from base-types.ts
// Keeping these as deprecated aliases for backward compatibility

export { WebhookSender as PostbackSender };
export { WebhookRecipient as PostbackRecipient };

/**
 * Represents referral information for postbacks that originated from external sources
 * 
 * This object is included when a postback is triggered as part of a conversation
 * that was started via m.me links, Click to Messenger ads, Messenger QR codes,
 * or the Welcome Screen.
 */
export interface PostbackReferral {
  /** 
   * Arbitrary data that was included in the original referral.
   * This is the custom parameter you set when creating the referral link.
   */
  ref?: string;

  /** 
   * Source of the referral. Indicates how the conversation was initiated.
   * Common values include:
   * - "SHORTLINK" - from m.me links
   * - "ADS" - from Click to Messenger ads
   * - "MESSENGER_CODE" - from Messenger QR codes
   */
  source?: string;

  ad_id?: string;
  ads_context_data?: {
    ad_title?: string;
    photo_url?: string;
    video_url?: string;
    post_id?: string;
  };

  /** 
   * Type of referral action that initiated the conversation.
   * Common value is "OPEN_THREAD" for most referral types.
   */
  type?: string;
}

/**
 * Represents the postback data in a messaging postback webhook event
 */
export interface PostbackData {
  /** 
   * Message ID associated with the postback.
   * Unique identifier for the message that contained the postback button.
   */
  mid?: string;

  /** 
   * Title of the postback button that was clicked.
   * This is the user-visible text that was displayed on the button.
   */
  title?: string;

  /** 
   * Developer-defined payload that was associated with the postback button.
   * This contains the custom data you specified when creating the button.
   * Maximum length is 1000 characters.
   */
  payload: string;

  /** 
   * Referral information if the postback is part of a referred conversation.
   * Only present when the conversation was initiated through external sources
   * like m.me links, ads, QR codes, or Welcome Screen.
   */
  referral?: PostbackReferral;
}

/**
 * Main webhook event structure for messaging postbacks with discriminator
 * 
 * This event is triggered when a user interacts with postback buttons,
 * including regular postback buttons, Get Started button, and persistent menu items.
 * 
 * @example
 * Basic postback from a button click:
 * ```json
 * {
 *   "type": "postback",
 *   "sender": {
 *     "id": "1234567890123456"
 *   },
 *   "recipient": {
 *     "id": "9876543210987654"
 *   },
 *   "timestamp": 1527459824,
 *   "postback": {
 *     "mid": "m_AG5Hz2Uq7tuwNEhXfYYKj8mJEM_QPpz5jdHtHaW",
 *     "title": "Get Started",
 *     "payload": "GET_STARTED_PAYLOAD"
 *   }
 * }
 * ```
 * 
 * @example
 * Postback with referral data from m.me link:
 * ```json
 * {
 *   "type": "postback",
 *   "sender": {
 *     "user_ref": "unique_ref_param"
 *   },
 *   "recipient": {
 *     "id": "9876543210987654"
 *   },
 *   "timestamp": 1527459824,
 *   "postback": {
 *     "title": "Contact Sales",
 *     "payload": "CONTACT_SALES",
 *     "referral": {
 *       "ref": "landing_page_ad_campaign",
 *       "source": "SHORTLINK",
 *       "type": "OPEN_THREAD"
 *     }
 *   }
 * }
 * ```
 */
export interface MessagingPostbackWebhookEvent extends BaseWebhookEvent {
  /** Discriminator for type narrowing */
  type: WebhookEventType.MESSAGING_POSTBACK;

  /** Details about the postback that was triggered */
  postback: PostbackData;
}

/**
 * Complete webhook payload structure that includes the messaging postback event
 * along with other webhook metadata
 */
export interface MessagingPostbackWebhookPayload extends WebhookPayload<MessagingPostbackWebhookEvent> { }

/**
 * Type guard to check if a webhook event is a messaging postback event
 * 
 * @param event - The webhook event to check
 * @returns True if the event contains a postback property
 * 
 * @example
 * ```typescript
 * if (isMessagingPostbackEvent(event)) {
 *   // TypeScript now knows event has postback property
 *   console.log(`User clicked: ${event.postback.title}`);
 *   console.log(`Payload: ${event.postback.payload}`);
 * }
 * ```
 */
export function isMessagingPostbackEvent(event: any): event is MessagingPostbackWebhookEvent {
  return event && typeof event === 'object' && 'postback' in event;
}

/**
 * Type guard to check if a postback event includes referral data
 * 
 * @param event - The messaging postback event to check
 * @returns True if the event contains referral information
 * 
 * @example
 * ```typescript
 * if (hasReferralData(event)) {
 *   // Handle referred conversation
 *   console.log(`Referred from: ${event.postback.referral.source}`);
 *   console.log(`Ref parameter: ${event.postback.referral.ref}`);
 * }
 * ```
 */
export function hasReferralData(event: MessagingPostbackWebhookEvent): event is MessagingPostbackWebhookEvent & {
  postback: PostbackData & { referral: PostbackReferral };
} {
  return event.postback && 'referral' in event.postback && event.postback.referral != null;
}

/**
 * Type guard to check if sender is identified (has PSID) vs anonymous (has user_ref)
 * 
 * @param sender - The sender object to check
 * @returns True if sender has a PSID (is identified)
 * 
 * @example
 * ```typescript
 * if (isIdentifiedSender(event.sender)) {
 *   // User has been identified and has a PSID
 *   console.log(`User PSID: ${event.sender.id}`);
 * } else {
 *   // Anonymous user with user_ref (likely from chat plugin)
 *   console.log(`Anonymous user ref: ${event.sender.user_ref}`);
 * }
 * ```
 */
export function isIdentifiedSender(sender: WebhookSender): sender is WebhookSender & { id: string } {
  return sender && typeof sender.id === 'string' && sender.id.length > 0;
}

/**
 * Utility type for extracting just the postback data from a webhook event
 */
export type PostbackEventData = PostbackData;

/**
 * Utility type for common postback properties that might be used in processing
 */
export interface PostbackProcessingContext {
  /** The postback payload data */
  payload: string;

  /** The user who triggered the postback (PSID or user_ref) */
  senderId?: string;

  /** User reference for anonymous users */
  userRef?: string;

  /** The page that received the postback */
  recipientId: string;

  /** Button title if available */
  buttonTitle?: string;

  /** Message ID if available */
  messageId?: string;

  /** When the postback occurred */
  timestamp: number;

  /** Referral context if present */
  referralContext?: {
    ref?: string;
    source?: string;
    type?: string;
  };

  /** Whether this is from a referred conversation */
  isReferred: boolean;

  /** Whether the sender is identified (has PSID) */
  isIdentifiedUser: boolean;
}

/**
 * Helper function to extract processing context from a messaging postback event
 * 
 * @param event - The messaging postback webhook event
 * @returns Simplified processing context with all relevant data
 * 
 * @example
 * ```typescript
 * const context = extractPostbackContext(webhookEvent);
 * 
 * if (context.isReferred) {
 *   console.log(`New conversation from ${context.referralContext?.source}`);
 * }
 * 
 * if (context.isIdentifiedUser) {
 *   console.log(`Known user ${context.senderId} clicked: ${context.payload}`);
 * } else {
 *   console.log(`Anonymous user ${context.userRef} clicked: ${context.payload}`);
 * }
 * ```
 */
export function extractPostbackContext(event: MessagingPostbackWebhookEvent): PostbackProcessingContext {
  const isReferred = hasReferralData(event);
  const isIdentifiedUser = isIdentifiedSender(event.sender);

  return {
    payload: event.postback.payload,
    senderId: event.sender.id,
    userRef: event.sender.user_ref,
    recipientId: event.recipient.id,
    buttonTitle: event.postback.title,
    messageId: event.postback.mid,
    timestamp: event.timestamp,
    referralContext: isReferred ? {
      ref: event.postback.referral.ref,
      source: event.postback.referral.source,
      type: event.postback.referral.type,
    } : undefined,
    isReferred,
    isIdentifiedUser,
  };
}

/**
 * Common postback payload patterns used in Messenger bots
 */
export const COMMON_POSTBACK_PAYLOADS = {
  /** Get Started button payload */
  GET_STARTED: 'GET_STARTED',

  /** Main menu navigation */
  MAIN_MENU: 'MAIN_MENU',

  /** Help/Support options */
  HELP: 'HELP',
  SUPPORT: 'SUPPORT',

  /** Contact information */
  CONTACT: 'CONTACT',
  CONTACT_SALES: 'CONTACT_SALES',
  CONTACT_SUPPORT: 'CONTACT_SUPPORT',

  /** Navigation actions */
  BACK: 'BACK',
  NEXT: 'NEXT',
  CANCEL: 'CANCEL',

  /** User preferences */
  SETTINGS: 'SETTINGS',
  PREFERENCES: 'PREFERENCES',
} as const;

/**
 * Constants related to postback events and constraints
 */
export const POSTBACK_CONSTANTS = {
  /** Maximum payload length in characters */
  MAX_PAYLOAD_LENGTH: 1000,

  /** Webhook event type identifier */
  EVENT_TYPE: 'postback' as const,

  /** Common referral sources */
  REFERRAL_SOURCES: {
    SHORTLINK: 'SHORTLINK',
    ADS: 'ADS',
    MESSENGER_CODE: 'MESSENGER_CODE',
  } as const,

  /** Common referral types */
  REFERRAL_TYPES: {
    OPEN_THREAD: 'OPEN_THREAD',
  } as const,
} as const;

/**
 * Type for postback payload values - can be custom strings or common patterns
 */
export type PostbackPayload = string | typeof COMMON_POSTBACK_PAYLOADS[keyof typeof COMMON_POSTBACK_PAYLOADS];

/**
 * Type for referral sources
 */
export type ReferralSource = typeof POSTBACK_CONSTANTS.REFERRAL_SOURCES[keyof typeof POSTBACK_CONSTANTS.REFERRAL_SOURCES] | string;

/**
 * Type for referral types  
 */
export type ReferralType = typeof POSTBACK_CONSTANTS.REFERRAL_TYPES[keyof typeof POSTBACK_CONSTANTS.REFERRAL_TYPES] | string;